import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  Popover, PopoverContent, PopoverTrigger,
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
  Skeleton,
} from "@nexus/ui";
import { cn } from "@nexus/ui";
import {
  GLOBAL_MARKET_CODE,
  marketEntryKey,
  type AddPreflightMarketInput,
  type PreflightMarketCatalogueEntry,
} from "@nexus/contracts";
import { allMarketCatalogueQueryOptions } from "@/lib/queries";
import { MarketIcon } from "@/features/preflight/components/MarketIcon/MarketIcon";

export interface MarketMultiSelectProps {
  selected: AddPreflightMarketInput[];
  onChange: (markets: AddPreflightMarketInput[]) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Accessible name for the trigger — a combobox takes no name from its content. */
  ariaLabel?: string;
}

/**
 * Multi-select over the Pre Flight market catalogue, mirroring the industry
 * multi-select it sits beside on the organisation form. Selection is a list of
 * `{kind, code}` refs — the payload the provisioning API validates — so no name
 * ever travels as an identity.
 */
export function MarketMultiSelect({
  selected,
  onChange,
  disabled = false,
  placeholder,
  ariaLabel,
}: MarketMultiSelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const marketsQuery = useQuery(allMarketCatalogueQueryOptions());
  const catalogue = marketsQuery.data ?? [];
  const selectableMarkets = useMemo(
    () =>
      catalogue.filter(
        (entry) => !(entry.kind === "GLOBAL" && entry.code === GLOBAL_MARKET_CODE),
      ),
    [catalogue],
  );
  const entryByKey = useMemo(
    () => new Map(selectableMarkets.map((entry) => [marketEntryKey(entry), entry])),
    [selectableMarkets],
  );
  const resolvedPlaceholder =
    placeholder ?? t("iam.shared.marketMultiSelect.placeholder", "Select markets…");

  const selectedKeys = new Set(selected.map(marketEntryKey));

  const toggle = (entry: PreflightMarketCatalogueEntry) => {
    const key = marketEntryKey(entry);
    if (selectedKeys.has(key)) {
      onChange(selected.filter((ref) => marketEntryKey(ref) !== key));
    } else {
      onChange([...selected, { kind: entry.kind, code: entry.code }]);
    }
  };

  const remove = (ref: AddPreflightMarketInput, e: React.MouseEvent) => {
    e.stopPropagation();
    const key = marketEntryKey(ref);
    onChange(selected.filter((current) => marketEntryKey(current) !== key));
  };

  const selectedEntries = selected.flatMap((ref) => {
    const entry = entryByKey.get(marketEntryKey(ref));
    return entry ? [entry] : [];
  });

  // Matches the code as well as the name, so typing `DE` reaches Germany while
  // the row keeps showing the country name.
  const filterByNameOrCode = (key: string, search: string) => {
    const entry = entryByKey.get(key);
    if (!entry) return 0;
    const haystack = `${entry.name} ${entry.code}`.toLowerCase();
    return haystack.includes(search.toLowerCase()) ? 1 : 0;
  };

  const triggerLabel = () => {
    if (selected.length === 0) return null;
    if (selected.length === 1) return selectedEntries[0]?.name ?? selected[0]?.code;
    return t("iam.shared.marketMultiSelect.triggerCount", "{{count}} markets", {
      count: selected.length,
    });
  };

  const label = triggerLabel();

  return (
    <div className="flex flex-col gap-1.5">
      <Popover open={open} onOpenChange={disabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-label={ariaLabel ?? t("iam.shared.marketMultiSelect.ariaLabel", "Markets")}
            className={cn(
              "flex h-9 w-full items-center justify-between gap-2 rounded-md border border-border bg-background px-3 text-sm outline-none transition-colors",
              "focus:border-ring focus:ring-1 focus:ring-ring/40",
              "disabled:cursor-not-allowed disabled:opacity-50",
              !label && "text-muted-foreground",
            )}
          >
            <span className="truncate">{label ?? resolvedPlaceholder}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 opacity-50" aria-hidden />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="w-(--radix-popover-trigger-width) p-0"
        >
          <Command filter={filterByNameOrCode}>
            <CommandInput
              placeholder={t("iam.shared.marketMultiSelect.searchPlaceholder", "Search markets…")}
            />
            <CommandList>
              {marketsQuery.isError ? (
                // A failed fetch is distinct from an empty catalogue — never fall
                // through to CommandEmpty, which would read as "no markets exist".
                <div
                  role="alert"
                  className="text-destructive px-4 py-6 text-center text-sm"
                >
                  {t(
                    "iam.shared.marketMultiSelect.error",
                    "Couldn't load markets. Try again.",
                  )}
                </div>
              ) : marketsQuery.isPending ? (
                <div
                  role="status"
                  aria-live="polite"
                  aria-label={t("iam.shared.marketMultiSelect.loading", "Loading markets")}
                  className="space-y-2 p-2"
                >
                  {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className="h-7 w-full" />
                  ))}
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    {t("iam.shared.marketMultiSelect.empty", "No markets found.")}
                  </CommandEmpty>
                  <CommandGroup>
                    {selectableMarkets.map((entry) => {
                      const key = marketEntryKey(entry);
                      const isSelected = selectedKeys.has(key);
                      return (
                        <CommandItem
                          key={key}
                          value={key}
                          onSelect={() => toggle(entry)}
                          aria-selected={isSelected}
                        >
                          <Checkbox
                            checked={isSelected}
                            className="mr-2 shrink-0"
                            aria-hidden
                          />
                          <MarketIcon kind={entry.kind} code={entry.code} name={entry.name} />
                          <span className="flex-1 truncate">{entry.name}</span>
                          {isSelected && (
                            <Check className="ml-auto h-3.5 w-3.5 shrink-0 opacity-70" aria-hidden />
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
            <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-[11px]">
              <span className="text-muted-foreground">
                {t("iam.shared.marketMultiSelect.selectedCount", "{{count}} selected", { count: selected.length })}
              </span>
            </div>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedEntries.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selectedEntries.map((entry) => (
            <span
              key={marketEntryKey(entry)}
              className="flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground"
            >
              <MarketIcon kind={entry.kind} code={entry.code} name={entry.name} />
              {entry.name}
              {!disabled && (
                <button
                  type="button"
                  aria-label={t("iam.shared.marketMultiSelect.removeMarket", "Remove {{name}}", { name: entry.name })}
                  onClick={(e) => remove(entry, e)}
                  className="ml-0.5 rounded hover:text-destructive focus:outline-none"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
